/**
 * Сборка диагностического задания.
 *
 * Задание собирается из частотных слов языка: измерять исходный уровень на
 * бессмысленных последовательностях бессмысленно — они не отражают реальный
 * набор (ТЗ §5, §6).
 *
 * Повторная диагностика рассчитывается на то же время, а не на то же число
 * слов (ТЗ FR-EN-63): тому, кто печатает быстро, двадцати четырёх слов хватает
 * на полминуты, и число получается шумным. Объём выводится из известной
 * скорости — `DIAGNOSTIC_WORD_COUNT` остаётся нижней границей.
 */

import type { LanguageCode, LayoutId } from '@type2x/shared-contracts';
import { getWords } from '@type2x/corpora';
import { createRng } from '@type2x/exercise-generator';

import type { DiagnosticTask } from '../contracts/diagnostics.js';

/**
 * Наименьшее число слов диагностики.
 *
 * Столько задание содержало всегда. Для новичка это около двух минут набора —
 * достаточно, чтобы измерение было устойчивым.
 */
export const DIAGNOSTIC_WORD_COUNT = 24;

/**
 * Наибольшее число слов диагностики.
 *
 * Тому, кто печатает быстро, двадцати четырёх слов мало: он проходит их за
 * полминуты, и число получается шумным. Значение выбрано по фактическим
 * прогонам: полторы минуты при четырёхстах знаках в минуту — это около ста
 * тридцати коротких слов. Кто быстрее, получит задание короче полутора минут,
 * и это не беда: у него измерение и так устойчивое.
 */
export const MAX_DIAGNOSTIC_WORDS = 140;

/**
 * Сколько нажатий нужно, чтобы числа считались измеренными, а не случайными.
 *
 * Самое короткое задание — двадцать четыре слова, это около полутора сотен
 * нажатий. Половина от него и есть граница: ниже неё итог показывается, но с
 * оговоркой, что выборка мала (ТЗ UI-39). Молчание об этом было бы обещанием
 * точности, которой нет.
 */
export const MIN_RELIABLE_KEYSTROKES = 72;

/** Сколько времени должно занимать задание, с. */
const TARGET_SECONDS = 90;

/**
 * Из скольких самых частых слов набирается задание.
 *
 * Пул больше самого длинного задания: иначе быстрый прошёл бы один и тот же
 * набор слов дважды подряд и мерил бы уже не набор, а память.
 */
const CANDIDATE_POOL_SIZE = 160;

/**
 * Собирает диагностическое задание.
 *
 * Детерминировано по seed: две попытки с одним seed дают один текст, что
 * позволяет сравнивать результаты и воспроизводить жалобы.
 */
export function buildDiagnosticTask(
  language: LanguageCode,
  layoutId: LayoutId,
  seed: string,
  knownCpm?: number,
): DiagnosticTask {
  const rng = createRng(seed);
  const candidates = getWords(language, { minLength: 2, limit: CANDIDATE_POOL_SIZE });

  const words: string[] = [];
  const pool = rng.shuffle(candidates.map((word) => word.text));

  const count = wordCount(pool, knownCpm);
  for (let index = 0; index < count; index += 1) {
    words.push(pool[index % pool.length]!);
  }

  return { language, layoutId, text: words.join(' '), words, seed };
}

/**
 * Сколько слов нужно, чтобы задание заняло отведённое время.
 *
 * Известная скорость есть только у того, кто уже проходил диагностику: первое
 * измерение считать не из чего, и оно остаётся прежней длины. Повторное
 * подстраивается — быстрому двадцати четырёх слов мало на устойчивое число.
 */
function wordCount(pool: readonly string[], knownCpm: number | undefined): number {
  if (knownCpm === undefined || knownCpm <= 0) return DIAGNOSTIC_WORD_COUNT;

  const averageLength = pool.reduce((total, word) => total + [...word].length, 0) / pool.length;
  // Пробел между словами тоже набирается и в скорость входит.
  const charactersNeeded = (knownCpm / 60) * TARGET_SECONDS;
  const count = Math.round(charactersNeeded / (averageLength + 1));

  return Math.min(Math.max(count, DIAGNOSTIC_WORD_COUNT), MAX_DIAGNOSTIC_WORDS);
}
